import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateForTopic } from './queries';

export type ConnectionState = 'verbunden' | 'getrennt' | 'verbindet';

const RECONNECT_DELAY_MS = 3000;

/**
 * Hält die Ansicht mit den anderen Arbeitsplätzen im Gleichstand.
 *
 * Ändert jemand im Büro etwas, meldet der Server das über einen offenen
 * Ereignis-Kanal; hier werden nur die betroffenen Abfragen neu geladen — nicht
 * die ganze Seite. Bricht die Verbindung ab, wird sie automatisch wieder
 * aufgebaut, und der Verbindungszustand wird in der Kopfzeile angezeigt.
 */
export function useLiveSync(enabled: boolean): ConnectionState {
  const client = useQueryClient();
  const [state, setState] = useState<ConnectionState>('verbindet');

  useEffect(() => {
    if (!enabled) {
      setState('getrennt');
      return;
    }

    let source: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let closed = false;

    const connect = () => {
      if (closed) return;
      setState((current) => (current === 'verbunden' ? current : 'verbindet'));

      source = new EventSource('/api/events');

      source.addEventListener('hello', () => setState('verbunden'));

      source.addEventListener('change', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data) as { topic?: string };
          if (data.topic) invalidateForTopic(client, data.topic);
        } catch {
          // Unlesbare Meldung: lieber alles neu laden als veraltet anzeigen.
          void client.invalidateQueries();
        }
      });

      source.onerror = () => {
        source?.close();
        source = null;
        setState('getrennt');
        if (!closed) reconnectTimer = window.setTimeout(connect, RECONNECT_DELAY_MS);
      };
    };

    connect();

    // Nach dem Aufwachen aus dem Ruhezustand kann der Kanal tot sein, ohne dass
    // ein Fehler ausgelöst wurde — deshalb beim Zurückkehren neu abgleichen.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void client.invalidateQueries();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      closed = true;
      window.clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisible);
      source?.close();
    };
  }, [enabled, client]);

  return state;
}
